import { api } from "@/utils/api";
import { FormEvent, useEffect, useState } from "react";
import { updateUser } from "@/features/user/userSlice";
import { useDispatch } from "react-redux";
import { User } from "@/types/userTypes";
import Select from "react-select";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
  getLatLng,
} from "react-google-places-autocomplete";
import Calendarcheck from "./calendar/Calendarcheck";
import { ProfileImageUpdater } from "../employer-profile/ProfileImageUpdater";
import { GoogleLocation } from "@/types/googleLocationTypes";

type OptionSkill = {
  label: string;
  value: string;
};

//! 🛑 IMPORTANTE 🛑 -> Para correr este componente en modo DESARROLLO hay que desactivar el React.StrictMode desde el main.tsx de la app.

export function UpdateProfileForm() {
  const [user, setUser] = useState<User>({});
  const [skillOptions, setSkillOptions] = useState<OptionSkill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<OptionSkill[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [googleLocation, setGoogleLocation] = useState<GoogleLocation>();
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  // @ts-expect-error: Error de tipado en import.meta (no lo encuentra)
  const googlePlacesApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

  useEffect(() => {
    const fetchInitialData = async () => {
      // API Calls
      const { services } = await api.getServices();
      const user = await api.getMe();
      setUser(user);

      const allSkills = [];
      const userSkills = user?.workerProfile?.skills || [];

      services.forEach((service) => {
        allSkills.push(...service.skills);
      });

      // Deduplicate skills by ID (if needed)
      const uniqueSkillsMap = new Map();
      allSkills.forEach((skill) => {
        if (!uniqueSkillsMap.has(skill.id)) {
          uniqueSkillsMap.set(skill.id, skill);
        }
      });

      const uniqueSkills = Array.from(uniqueSkillsMap.values());

      const skillOptions = uniqueSkills.map((skill) => ({
        label: skill.name,
        value: skill.id,
      }));

      const selected = skillOptions.filter((option) =>
        userSkills.includes(option.value)
      );

      setSkillOptions(skillOptions);
      setSelectedSkills(selected);
    };

    fetchInitialData();
  }, []);

  const handleUpdateProfile = async (e: FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setIsLoading(true);

      const htmlForm = e.target as HTMLFormElement;
      const formData = new FormData(htmlForm);
      const avatarImg = formData.get("file") as File;

      const calendarUrl = formData.get("calendar_url") as string;

      if (calendarUrl && !calendarUrl.startsWith("https://cal.com/")) {
        const error = "Formato invalido de calendario";
        throw new Error(error);
      }

      const userData = {
        first_name: formData.get("first_name") as string,
        last_name: formData.get("last_name") as string,
        phone: formData.get("phone") as string,
        email: formData.get("email") as string,
        calendar_url: formData.get("calendar_url") as string,
      };

      await api.updateProfile(userData);

      let location = undefined;
      if (googleLocation) {
        const [geocode] = await geocodeByPlaceId(
          googleLocation?.value.place_id
        );
        const { lat, lng } = await getLatLng(geocode);
        location = {
          type: "Point",
          coordinates: [lng, lat],
        };
      }

      const workerData = {
        hourlyPay: htmlForm.hourlyPay.value,
        skills: selectedSkills.map((skill) => skill.value),
        description: htmlForm.description.value,
        location,
        locationName: googleLocation?.value.terms
          .slice(0, 2)
          .map((term) => term.value)
          .join(", "),
        address: {
          line1: htmlForm.street.value,
          line2: htmlForm.streetNumber.value,
          zipCode: htmlForm.zipCode.value,
        },
      };
      await api.updateMyWorkerProfile(workerData);

      if (avatarImg?.size) {
        const avatarForm = new FormData();
        avatarForm.set("file", avatarImg);

        const updatedUser = await api.updateAvatarPhoto(avatarForm);
        dispatch(updateUser(updatedUser));
      }
      resetError();
    } catch (error) {
      console.log(error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetError = () => {
    setError(null);
  };

  return (
    <form onSubmit={handleUpdateProfile} className="default-form">
      <ProfileImageUpdater />
      <div className="row">
        {/* <!-- Input --> */}
        <div className="form-group col-lg-6 col-md-12">
          <label>Nombre</label>
          <input
            type="text"
            name="first_name"
            defaultValue={user?.first_name}
            placeholder="Ingrese su nombre"
            required
          />
        </div>

        {/* <!-- Input --> */}
        <div className="form-group col-lg-6 col-md-12">
          <label>Apellido</label>
          <input
            type="text"
            name="last_name"
            defaultValue={user?.last_name}
            placeholder="Ingrese su apellido"
            required
          />
        </div>

        {/* <!-- Input --> */}
        <div className="form-group col-lg-6 col-md-12">
          <label>Teléfono</label>
          <input
            type="text"
            name="phone"
            defaultValue={user?.phone}
            placeholder="Ingrese su número de teléfono"
            required
          />
        </div>

        {/* <!-- Input --> */}
        <div className="form-group col-lg-6 col-md-12">
          <label>Email</label>
          <input
            type="email"
            name="email"
            defaultValue={user?.email}
            placeholder="Ingrese su email"
            required
          />
        </div>

        {/* <!-- Input --> */}
        <div className="form-group col-lg-6 col-md-12">
          <label>Valor por hora</label>
          <input
            type="number"
            name="hourlyPay"
            defaultValue={user?.workerProfile?.hourlyPay}
            placeholder="Ingrese el valor por hora"
            required
            onWheel={(e) => e.currentTarget.blur()}
          />
        </div>

        <div className="form-group col-lg-6 col-md-12">
          <label>Ciudad</label>
          <GooglePlacesAutocomplete
            apiKey={googlePlacesApiKey}
            selectProps={{
              value: googleLocation,
              onChange: setGoogleLocation,
              className: "basic-multi-select",
              classNamePrefix: "select",
              placeholder: user?.workerProfile?.locationName,
            }}
          />
        </div>

        <div className="form-group col-lg-6 col-md-12">
          <label>Dirección</label>
          <div className="d-flex gap-3">
            <input
              type="text"
              name="street"
              defaultValue={user?.workerProfile?.address?.line1}
              placeholder="Ingrese el nombre de la calle"
              required
            />
            <input
              type="number"
              name="streetNumber"
              defaultValue={user?.workerProfile?.address?.line2}
              placeholder="Ingrese el número de la calle"
              required
              onWheel={(e) => e.currentTarget.blur()}
            />
          </div>
        </div>

        <div className="form-group col-lg-6 col-md-12">
          <label>Código Postal</label>
          <input
            type="number"
            name="zipCode"
            defaultValue={user?.workerProfile?.address?.zipCode}
            placeholder="Ingrese el código postal"
            required
            onWheel={(e) => e.currentTarget.blur()}
          />
        </div>

        {/* <!-- Search Select --> */}
        <div className="form-group col-lg-12 col-md-12">
          <label>Servicios</label>
          <Select
            value={[...selectedSkills]}
            isMulti
            name="skills"
            options={skillOptions}
            placeholder="Ingrese sus servicios"
            onChange={(selection) => {
              setSelectedSkills([...selection]);
            }}
            className="basic-multi-select"
            classNamePrefix="select"
            required
          />
        </div>

        {/* <!-- About Company --> */}
        <div className="form-group col-lg-12 col-md-12">
          <label>Cuéntanos sobre tí</label>
          <textarea
            name="description"
            placeholder="Añade una descripción para tu perfil"
            defaultValue={user?.workerProfile?.description}
          ></textarea>
        </div>

        <Calendarcheck resetError={resetError} />

        {/* <!-- Input --> */}
        <div className="form-group col-lg-12 col-md-12 text-end">
          <p style={{ marginTop: 15 }}>{error}</p>
          <button
            type="submit"
            className="theme-btn btn-style-one"
            disabled={isLoading}
          >
            {isLoading ? (
              <div
                className="spinner-border"
                style={{ height: "15px", width: "15px" }}
                role="status"
              ></div>
            ) : (
              "Guardar"
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
